import { Form, Formik } from 'formik'
import { FC, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Col from 'react-bootstrap/Col'
import Row from 'react-bootstrap/Row'
import Stack from 'react-bootstrap/Stack'
import { FormattedMessage, useIntl } from 'react-intl'

import { NRICCardOCRAPI } from '@api/nricCardOcrAPI'

import { GenderEnum, ID } from '@models/base'
import { MaritalStatusEnum } from '@models/base'
import { NRICCardOCRModel } from '@models/nricCardOcr'
import { Option, WithIdv2 } from '@models/option'

import { Button } from '@components/Button'
import { AdministrativeDivisionInputField } from '@components/Form/AdministrativeDivisionInput'
import { BloodTypeSelectField } from '@components/Form/BloodTypeSelectField/BloodTypeSelectField'
import { DatePickerInputField } from '@components/Form/DatePicker/DatePickerField'
import { GenderRadioButtonField } from '@components/Form/GenderRadioButton'
import { ImageUploadField } from '@components/Form/ImageUpload'
import { Input, InputField } from '@components/Form/Input'
import { MaritalStatusSelectField } from '@components/Form/MaritalStatusSelectField/MaritalStatusSelectField'
import { PhoneInputField } from '@components/Form/PhoneInput/PhoneInputField'
import { PostCodeInputField } from '@components/Form/PostalCodeInput'
import { RadioButtonGroup, RadioButtonGroupField } from '@components/Form/RadioButtonGroup'
import { ReligionSelectField } from '@components/Form/ReligionSelectField'
import { TextareaField } from '@components/Form/Textarea'
import { FormControl } from '@components/FormControl'
import { FormSection } from '@components/FormSection'
import { Tooltip } from '@components/Tooltip'

import { CountrySelectField } from '@modules/country/components/CountrySelectField'
import { NRICExtractor } from '@modules/patients/components/NRICExtractor'

import { useScrollIntoView } from '@/hooks/useScrollIntoView'
import dayjs from '@/libs/dayjs'
import PromiseUtil from '@/utils/PromiseUtil'
import AxiosUtil from '@/utils/axiosUtil'
import FormUtil, { FormShape } from '@/utils/formUtil'
import { CheckboxSwitchField } from '@components/Form/CheckboxSwitch'
import YesNoRadioButtonGroupField from '@components/Form/YesNoRadioButtonGroupField'

type Props = {
  patientId?: ID
  initialValues?: PatientFormShape
  onCancel: () => void
  onSubmit: (values: PatientFormShape) => Promise<void>
}

type PatientFormShape = FormShape<{
  first_name: string
  birth_date: string
  blood_type: Option
  business_phone_number: string
  family_card_number: string
  gender: GenderEnum
  home_phone_number: string
  is_asian: boolean
  is_organ_donor: boolean
  is_signed_satusehat_consent: boolean
  language: string
  last_name: string
  living_country: Option<WithIdv2>
  living_first_level_administrative_division_id: Option
  living_second_level_administrative_division_id: Option
  living_third_level_administrative_division_id: Option
  living_fourth_level_administrative_division_id: Option
  living_postal_code: string
  living_address_1: string
  living_address_2: string
  living_rt: string
  living_rw: string
  marital_status: Option
  medical_notes: string
  mobile_phone_number: string
  nric_address_1: string
  nric_address_2: string
  nric_rt: string
  nric_rw: string
  nric_country: Option<WithIdv2>
  nric_file_id: ID
  nric_first_level_administrative_division_id: Option
  nric_second_level_administrative_division_id: Option
  nric_third_level_administrative_division_id: Option
  nric_fourth_level_administrative_division_id: Option
  nric_postal_code: string
  nric_number: string
  passport_number: string
  religion: Option
}>

const checkIsSameAddress = (values: PatientFormShape): boolean => {
  return (
    values.nric_country?.value === values.living_country?.value &&
    values.nric_first_level_administrative_division_id?.value ===
      values.living_first_level_administrative_division_id?.value &&
    values.nric_second_level_administrative_division_id?.value ===
      values.living_second_level_administrative_division_id?.value &&
    values.nric_third_level_administrative_division_id?.value ===
      values.living_third_level_administrative_division_id?.value &&
    values.nric_fourth_level_administrative_division_id?.value ===
      values.living_fourth_level_administrative_division_id?.value &&
    values.nric_postal_code === values.living_postal_code &&
    values.nric_address_1 === values.living_address_1 &&
    values.nric_address_2 === values.living_address_2 &&
    values.nric_rt === values.living_rt &&
    values.nric_rw === values.living_rw
  )
}

const PatientForm: FC<Props> = ({ patientId, initialValues, onSubmit, onCancel }) => {
  const today = dayjs().toTzISOString()
  const intl = useIntl()
  const [error, setError] = useState<string>()
  const [isSameAddress, setIsSameAddress] = useState<boolean>(initialValues ? checkIsSameAddress(initialValues) : false)
  const [scrollRef, scrollIntoView] = useScrollIntoView<HTMLFormElement>()

  const defaultValues: PatientFormShape = {
    is_asian: true,
    is_signed_satusehat_consent: false,
  }

  const parseNRICCardToFormShape = (nric: NRICCardOCRModel): PatientFormShape => {
    const maritalStatus = nric.is_married ? MaritalStatusEnum.MARRIED : MaritalStatusEnum.SINGLE
    return {
      first_name: nric.name,
      nric_number: nric.nric_number,
      birth_date: nric.birth_date,
      nric_address_1: nric.address,
      gender: nric.gender,
      nric_postal_code: nric.postal_code?.postal_code,
      nric_country: nric.country ? Option.fromString(nric.country.name, { id: nric.country.id }) : undefined,
      blood_type: nric.blood_type ? Option.fromString(nric.blood_type) : undefined,
      religion: nric.religion
        ? new Option({
            label: intl.formatMessage({ id: `enum.religion.${nric.religion.toLowerCase()}` }),
            value: nric.religion,
          })
        : undefined,
      marital_status: nric.is_married
        ? new Option({
            label: intl.formatMessage({ id: `enum.marital_status.${maritalStatus.toLowerCase()}` }),
            value: maritalStatus,
          })
        : undefined,
    }
  }

  return (
    <Formik<PatientFormShape>
      validateOnChange={false}
      initialValues={initialValues ?? defaultValues}
      onSubmit={async (values, { setSubmitting, setErrors }) => {
        try {
          setSubmitting(true)
          await onSubmit(values)
        } catch (err) {
          if (AxiosUtil.isAxiosError(err)) {
            const errors = FormUtil.parseErrorResponse(err.response?.data.errors)
            if (errors) {
              setErrors(errors)
            } else {
              setError(err.response?.data.message)
            }
            scrollIntoView({ behavior: 'smooth' })
          }
        } finally {
          setSubmitting(false)
        }
      }}
    >
      {({ values, isSubmitting, dirty, errors, setValues, setFieldValue, handleSubmit }) => (
        <Form
          ref={scrollRef}
          onSubmit={handleSubmit}
          noValidate
        >
          {error && (
            <Alert
              variant="danger"
              className="mb-10"
            >
              {error}
            </Alert>
          )}

          <FormSection
            title={intl.formatMessage({ id: 'patient.form_nric_scanner.title' })}
            description={intl.formatMessage({ id: 'patient.form_nric_scanner.description' })}
          >
            <NRICExtractor
              onExtracted={async (nric, file) => {
                setValues({
                  ...values,
                  ...parseNRICCardToFormShape(nric),
                  nric_file_id: file.id,
                })
                setFieldValue(
                  'nric_first_level_administrative_division_id',
                  nric.first_level_administrative_division
                    ? Option.fromObject(nric.first_level_administrative_division, 'id', 'name')
                    : undefined
                )
                await PromiseUtil.delay(50)
                setFieldValue(
                  'nric_second_level_administrative_division_id',
                  nric.second_level_administrative_division
                    ? Option.fromObject(nric.second_level_administrative_division, 'id', 'name')
                    : undefined
                )
                await PromiseUtil.delay(50)
                setFieldValue(
                  'nric_third_level_administrative_division_id',
                  nric.third_level_administrative_division
                    ? Option.fromObject(nric.third_level_administrative_division, 'id', 'name')
                    : undefined
                )
                await PromiseUtil.delay(50)
                setFieldValue(
                  'nric_fourth_level_administrative_division_id',
                  nric.fourth_level_administrative_division
                    ? Option.fromObject(nric.fourth_level_administrative_division, 'id', 'name')
                    : undefined
                )
              }}
            />
          </FormSection>

          <FormSection
            title={intl.formatMessage({ id: 'patient.form_manual_nric_scanner.title' })}
            description={intl.formatMessage({ id: 'patient.form_manual_nric_scanner.description' })}
          >
            <ImageUploadField
              name="nric_file_id"
              accept={['image/jpg', 'image/jpeg', 'image/jfif', 'image/png']}
              aspectRatio={3 / 2}
              uploadFn={NRICCardOCRAPI.upload}
              accessor={({ data }) => data?.file.id}
            />
          </FormSection>

          <FormSection
            title={intl.formatMessage({ id: 'patient.form_patient_identifier_title' })}
            description={intl.formatMessage({ id: 'patient.form_patient_identifier_description' })}
          >
            <Row>
              <Col xs={12}>
                <Row>
                  {patientId && (
                    <Col xl={6}>
                      <FormControl>
                        <FormControl.Label isRequired>
                          <FormattedMessage id="patient.id" />
                        </FormControl.Label>
                        <Input
                          isDisabled
                          value={patientId}
                        />
                      </FormControl>
                    </Col>
                  )}

                  <Col xl={6}>
                    <FormControl>
                      <FormControl.Label>
                        <FormattedMessage id="patient.mobile_phone_number" />
                      </FormControl.Label>
                      <PhoneInputField name="mobile_phone_number" />
                      <FormControl.ErrorMessage message={errors.mobile_phone_number} />
                    </FormControl>
                  </Col>
                </Row>
              </Col>

              <Col xl={6}>
                <FormControl>
                  <FormControl.Label isRequired>
                    <FormattedMessage id="patient.nik" />
                    <Tooltip
                      text={intl.formatMessage({ id: 'patient.form.tooltip.ktp_passport' })}
                      placement="top"
                    >
                      <i className="fa-solid fa-info-circle ms-1" />
                    </Tooltip>
                  </FormControl.Label>
                  <InputField name="nric_number" />
                  <FormControl.ErrorMessage message={errors.nric_number} />
                </FormControl>
              </Col>

              <Col xl={6}>
                <FormControl>
                  <FormControl.Label isRequired>
                    <FormattedMessage id="passport_number" />
                    <Tooltip
                      text={intl.formatMessage({ id: 'patient.form.tooltip.ktp_passport' })}
                      placement="top"
                    >
                      <i className="fa-solid fa-info-circle ms-1" />
                    </Tooltip>
                  </FormControl.Label>
                  <InputField name="passport_number" />
                  <FormControl.ErrorMessage message={errors.passport_number} />
                </FormControl>
              </Col>

              <Col xl={6}>
                <FormControl>
                  <FormControl.Label isRequired>
                    <FormattedMessage id="vocabulary.first_name" />
                  </FormControl.Label>
                  <InputField name="first_name" />
                  <FormControl.ErrorMessage message={errors.first_name} />
                </FormControl>
              </Col>

              <Col xl={6}>
                <FormControl>
                  <FormControl.Label>
                    <FormattedMessage id="vocabulary.last_name" />
                  </FormControl.Label>
                  <InputField name="last_name" />
                  <FormControl.ErrorMessage message={errors.last_name} />
                </FormControl>
              </Col>

              <Col xl={6}>
                <FormControl>
                  <FormControl.Label isRequired>
                    <FormattedMessage id="vocabulary.birth_date" />
                  </FormControl.Label>
                  <DatePickerInputField
                    name="birth_date"
                    maxDate={today}
                  />
                  <FormControl.ErrorMessage message={errors.birth_date} />
                </FormControl>
              </Col>

              <Col xl={6}>
                <FormControl>
                  <FormControl.Label isRequired>
                    <FormattedMessage id="patient.religion" />
                  </FormControl.Label>
                  <ReligionSelectField name="religion" />
                  <FormControl.ErrorMessage message={errors.religion} />
                </FormControl>
              </Col>

              <Col xl={6}>
                <FormControl>
                  <FormControl.Label isRequired>
                    <FormattedMessage id="vocabulary.gender" />
                  </FormControl.Label>
                  <GenderRadioButtonField name="gender" />
                  <FormControl.ErrorMessage message={errors.gender} />
                </FormControl>
              </Col>
            </Row>
          </FormSection>

          <FormSection
            title={intl.formatMessage({ id: 'patient.form_general_title' })}
            description={intl.formatMessage({ id: 'patient.form_general_description' })}
          >
            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="patient.blood_type" />
              </FormControl.Label>
              <BloodTypeSelectField name="blood_type" />
              <FormControl.ErrorMessage message={errors.blood_type} />
            </FormControl>

            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="marital_status" />
              </FormControl.Label>
              <MaritalStatusSelectField name="marital_status" />
              <FormControl.ErrorMessage message={errors.marital_status} />
            </FormControl>

            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="language" />
              </FormControl.Label>
              <InputField name="language" />
              <FormControl.ErrorMessage message={errors.language} />
            </FormControl>

            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="patient.patient_notes" />
              </FormControl.Label>
              <TextareaField
                name="medical_notes"
                rows={5}
              />
              <FormControl.ErrorMessage message={errors.medical_notes} />
            </FormControl>

            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="patient.asian" />
              </FormControl.Label>
              <YesNoRadioButtonGroupField name="is_asian" />
              <FormControl.ErrorMessage message={errors.is_asian} />
            </FormControl>

            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="organ_donor" />
              </FormControl.Label>
              <RadioButtonGroupField
                name="is_organ_donor"
                options={[
                  {
                    label: intl.formatMessage({
                      id: 'patient.organ_donor_radio_not_stated',
                    }),
                    value: null,
                  },
                  {
                    label: intl.formatMessage({
                      id: 'patient.organ_donor_radio_yes',
                    }),
                    value: true,
                  },
                  {
                    label: intl.formatMessage({
                      id: 'patient.organ_donor_radio_no',
                    }),
                    value: false,
                  },
                ]}
              />
              <FormControl.ErrorMessage message={errors.is_organ_donor} />
            </FormControl>
          </FormSection>

          <FormSection
            title={intl.formatMessage({ id: 'patient.nric.title' })}
            description={intl.formatMessage({ id: 'patient.nric.description' })}
          >
            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="patient.kk" />
              </FormControl.Label>
              <InputField name="family_card_number" />
              <FormControl.ErrorMessage message={errors.family_card_number} />
            </FormControl>

            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="patient.address" />
              </FormControl.Label>
              <Stack gap={3}>
                <InputField
                  name="nric_address_1"
                  onChange={(value) => {
                    if (isSameAddress) {
                      setFieldValue('living_address_1', value)
                    }
                  }}
                />
                <InputField
                  name="nric_address_2"
                  onChange={(value) => {
                    if (isSameAddress) {
                      setFieldValue('living_address_2', value)
                    }
                  }}
                />
              </Stack>
              <FormControl.ErrorMessage message={errors.nric_address_1 || errors.nric_address_2} />
            </FormControl>

            <Row sm={2}>
              <Col
                md={6}
                lg={4}
                xl={3}
              >
                <FormControl>
                  <FormControl.Label>
                    <FormattedMessage id="vocabulary.rt" />
                  </FormControl.Label>
                  <InputField
                    name="nric_rt"
                    onChange={(value) => {
                      if (isSameAddress) {
                        setFieldValue('living_rt', value)
                      }
                    }}
                  />
                  <FormControl.ErrorMessage message={errors.nric_rt} />
                </FormControl>
              </Col>
              <Col
                md={6}
                lg={4}
                xl={3}
              >
                <FormControl>
                  <FormControl.Label>
                    <FormattedMessage id="vocabulary.rw" />
                  </FormControl.Label>
                  <InputField
                    name="nric_rw"
                    onChange={(value) => {
                      if (isSameAddress) {
                        setFieldValue('living_rw', value)
                      }
                    }}
                  />
                  <FormControl.ErrorMessage message={errors.nric_rw} />
                </FormControl>
              </Col>
            </Row>

            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="country" />
              </FormControl.Label>
              <CountrySelectField
                name="nric_country"
                onChange={(value) => {
                  setFieldValue('nric_first_level_administrative_division_id', undefined)
                  if (isSameAddress) {
                    setFieldValue('living_country', value)
                  }
                }}
              />
              <FormControl.ErrorMessage message={errors.nric_country} />
            </FormControl>

            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="province" />
              </FormControl.Label>
              <AdministrativeDivisionInputField
                name="nric_first_level_administrative_division_id"
                countryId={values.nric_country?.data?.id}
                onChange={(value) => {
                  setFieldValue('nric_second_level_administrative_division_id', undefined)
                  if (isSameAddress) {
                    setFieldValue('living_first_level_administrative_division_id', value)
                  }
                }}
              />
              <FormControl.ErrorMessage message={errors.nric_first_level_administrative_division_id} />
            </FormControl>

            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="city" />
              </FormControl.Label>
              <AdministrativeDivisionInputField
                name="nric_second_level_administrative_division_id"
                countryId={values.nric_country?.data?.id}
                parentId={values.nric_first_level_administrative_division_id?.value ?? null}
                onChange={(value) => {
                  setFieldValue('nric_third_level_administrative_division_id', undefined)
                  if (isSameAddress) {
                    setFieldValue('living_second_level_administrative_division_id', value)
                  }
                }}
              />
              <FormControl.ErrorMessage message={errors.nric_second_level_administrative_division_id} />
            </FormControl>

            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="district" />
              </FormControl.Label>
              <AdministrativeDivisionInputField
                name="nric_third_level_administrative_division_id"
                countryId={values.nric_country?.data?.id}
                parentId={values.nric_second_level_administrative_division_id?.value ?? null}
                onChange={(value) => {
                  setFieldValue('nric_fourth_level_administrative_division_id', undefined)
                  if (isSameAddress) {
                    setFieldValue('living_third_level_administrative_division_id', value)
                  }
                }}
              />
              <FormControl.ErrorMessage message={errors.nric_third_level_administrative_division_id} />
            </FormControl>

            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="sub_district" />
              </FormControl.Label>
              <AdministrativeDivisionInputField
                name="nric_fourth_level_administrative_division_id"
                countryId={values.nric_country?.data?.id}
                parentId={values.nric_third_level_administrative_division_id?.value ?? null}
                onChange={(value) => {
                  if (isSameAddress) {
                    setFieldValue('living_fourth_level_administrative_division_id', value)
                  }
                }}
              />
              <FormControl.ErrorMessage message={errors.nric_fourth_level_administrative_division_id} />
            </FormControl>

            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="postal_code" />
              </FormControl.Label>
              <PostCodeInputField
                name="nric_postal_code"
                onChange={(value) => {
                  if (isSameAddress) {
                    setFieldValue('living_postal_code', value)
                  }
                }}
              />
              <FormControl.ErrorMessage message={errors.nric_postal_code} />
            </FormControl>
          </FormSection>

          <FormSection
            title={intl.formatMessage({ id: 'patient.address.title' })}
            description={intl.formatMessage({
              id: 'patient.address.description',
            })}
          >
            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="same_as_ktp" />
              </FormControl.Label>
              <RadioButtonGroup
                name="is_same_address"
                value={isSameAddress}
                options={[
                  {
                    label: intl.formatMessage({ id: 'vocabulary.yes' }),
                    value: true,
                  },
                  {
                    label: intl.formatMessage({ id: 'vocabulary.no' }),
                    value: false,
                  },
                ]}
                onChange={async (value) => {
                  setIsSameAddress(value === true)
                  if (value === true) {
                    setFieldValue('living_address_1', values.nric_address_1)
                    setFieldValue('living_address_2', values.nric_address_2)
                    setFieldValue('living_rt', values.nric_rt)
                    setFieldValue('living_rw', values.nric_rw)
                    setFieldValue('living_country', values.nric_country)
                    await PromiseUtil.delay(50)
                    setFieldValue(
                      'living_first_level_administrative_division_id',
                      values.nric_first_level_administrative_division_id
                    )
                    await PromiseUtil.delay(50)
                    setFieldValue(
                      'living_second_level_administrative_division_id',
                      values.nric_second_level_administrative_division_id
                    )
                    await PromiseUtil.delay(50)
                    setFieldValue(
                      'living_third_level_administrative_division_id',
                      values.nric_third_level_administrative_division_id
                    )
                    await PromiseUtil.delay(50)
                    setFieldValue(
                      'living_fourth_level_administrative_division_id',
                      values.nric_fourth_level_administrative_division_id
                    )
                    setFieldValue('living_postal_code', values.nric_postal_code)
                  }
                }}
              />
            </FormControl>

            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="patient.address" />
              </FormControl.Label>
              <Stack gap={3}>
                <InputField
                  name="living_address_1"
                  isDisabled={isSameAddress}
                />
                <InputField
                  name="living_address_2"
                  isDisabled={isSameAddress}
                />
              </Stack>
              <FormControl.ErrorMessage message={errors.living_address_1 || errors.living_address_2} />
            </FormControl>

            <Row sm={2}>
              <Col
                md={6}
                lg={4}
                xl={3}
              >
                <FormControl>
                  <FormControl.Label>
                    <FormattedMessage id="vocabulary.rt" />
                  </FormControl.Label>
                  <InputField
                    name="living_rt"
                    isDisabled={isSameAddress}
                  />
                  <FormControl.ErrorMessage message={errors.living_rt} />
                </FormControl>
              </Col>
              <Col
                md={6}
                lg={4}
                xl={3}
              >
                <FormControl>
                  <FormControl.Label>
                    <FormattedMessage id="vocabulary.rw" />
                  </FormControl.Label>
                  <InputField
                    name="living_rw"
                    isDisabled={isSameAddress}
                  />
                  <FormControl.ErrorMessage message={errors.living_rw} />
                </FormControl>
              </Col>
            </Row>

            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="country" />
              </FormControl.Label>
              <CountrySelectField
                name="living_country"
                isDisabled={isSameAddress}
                onChange={() => {
                  setFieldValue('living_first_level_administrative_division_id', undefined)
                }}
              />
              <FormControl.ErrorMessage message={errors.living_country} />
            </FormControl>

            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="province" />
              </FormControl.Label>
              <AdministrativeDivisionInputField
                name="living_first_level_administrative_division_id"
                countryId={values.living_country?.data?.id}
                isDisabled={isSameAddress}
                onChange={() => {
                  setFieldValue('living_second_level_administrative_division_id', undefined)
                }}
              />
              <FormControl.ErrorMessage message={errors.living_first_level_administrative_division_id} />
            </FormControl>

            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="city" />
              </FormControl.Label>
              <AdministrativeDivisionInputField
                name="living_second_level_administrative_division_id"
                countryId={values.living_country?.data?.id}
                parentId={values.living_first_level_administrative_division_id?.value ?? null}
                isDisabled={isSameAddress}
                onChange={() => {
                  setFieldValue('living_third_level_administrative_division_id', undefined)
                }}
              />
              <FormControl.ErrorMessage message={errors.living_second_level_administrative_division_id} />
            </FormControl>

            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="district" />
              </FormControl.Label>
              <AdministrativeDivisionInputField
                name="living_third_level_administrative_division_id"
                countryId={values.living_country?.data?.id}
                parentId={values.living_second_level_administrative_division_id?.value ?? null}
                isDisabled={isSameAddress}
                onChange={() => {
                  setFieldValue('living_fourth_level_administrative_division_id', undefined)
                }}
              />
              <FormControl.ErrorMessage message={errors.living_third_level_administrative_division_id} />
            </FormControl>

            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="sub_district" />
              </FormControl.Label>
              <AdministrativeDivisionInputField
                name="living_fourth_level_administrative_division_id"
                countryId={values.living_country?.data?.id}
                parentId={values.living_third_level_administrative_division_id?.value ?? null}
                isDisabled={isSameAddress}
              />
              <FormControl.ErrorMessage message={errors.living_fourth_level_administrative_division_id} />
            </FormControl>

            <FormControl>
              <FormControl.Label>
                <FormattedMessage id="postal_code" />
              </FormControl.Label>
              <PostCodeInputField
                name="living_postal_code"
                isDisabled={isSameAddress}
              />
              <FormControl.ErrorMessage message={errors.living_postal_code} />
            </FormControl>
          </FormSection>

          <FormSection
            title={intl.formatMessage({ id: 'patient.contact.title' })}
            description={intl.formatMessage({ id: 'patient.contact.description' })}
          >
            <div className="row">
              <div className="col-xl-6">
                <FormControl>
                  <FormControl.Label>
                    <FormattedMessage id="patient.home_phone_number" />
                  </FormControl.Label>
                  <PhoneInputField name="home_phone_number" />
                  <FormControl.ErrorMessage message={errors.home_phone_number} />
                </FormControl>
              </div>

              <div className="col-xl-6">
                <FormControl>
                  <FormControl.Label>
                    <FormattedMessage id="patient.business_home_number" />
                  </FormControl.Label>
                  <PhoneInputField name="business_phone_number" />
                  <FormControl.ErrorMessage message={errors.business_phone_number} />
                </FormControl>
              </div>
            </div>
          </FormSection>

          <FormSection
            title={null}
            description={null}
            isEndOfChild
          >
            <Row>
              <Col>
                <FormControl>
                  <CheckboxSwitchField
                    style={{
                      alignItems: 'normal',
                    }}
                    name="is_signed_satusehat_consent"
                    label={intl.formatMessage({ id: 'patient.label.satusehat_consent' })}
                  />
                  <FormControl.ErrorMessage message={errors.is_signed_satusehat_consent} />
                </FormControl>
              </Col>
            </Row>
          </FormSection>

          <div className="d-flex justify-content-end gap-3 mt-10">
            <Button
              theme="light"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              <FormattedMessage id="form.discard" />
            </Button>
            <Button
              type="submit"
              theme="primary"
              disabled={!dirty}
              isLoading={isSubmitting}
            >
              <FormattedMessage id="form.submit" />
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export type { PatientFormShape }

export { PatientForm }
